import Interaction, { InteractionType } from "../models/interactions/Interaction";
import { ISequence } from "../models/interactions/SequenceModel";

/**
 * Follows a sequence graph to the state each path leaves behind. An effect is
 * any string a node can produce; NO_EFFECT keeps whatever state the path
 * started with. Shared by the bubble target lookup and the take end state.
 */
export const NO_EFFECT = "none";

export type InteractionEffect = (interaction: Interaction) => string;

const getOutgoingByNodeId = (sequence: ISequence) => {
  const outgoingByNodeId = new Map<string, string[]>();
  (sequence.edges ?? []).forEach((edge) => {
    const targets = outgoingByNodeId.get(edge.source) ?? [];
    targets.push(edge.target);
    outgoingByNodeId.set(edge.source, targets);
  });
  return outgoingByNodeId;
};

export const getSequenceBranchTerminalEffects = (
  sequence: ISequence,
  startNodeId: string,
  effectOf: InteractionEffect,
  initialEffect: string,
): Set<string> => {
  const nodesById = new Map(
    (sequence.nodes ?? []).map((node) => [node.id, node]),
  );
  const outgoingByNodeId = getOutgoingByNodeId(sequence);
  const visited = new Map<string, Set<string>>();
  const terminalEffects = new Set<string>();
  const queue: Array<{ nodeId: string; effect: string }> = [];

  const enqueue = (nodeId: string, effect: string) => {
    if (!nodesById.has(nodeId)) return;
    const effects = visited.get(nodeId) ?? new Set<string>();
    if (effects.has(effect)) return;
    effects.add(effect);
    visited.set(nodeId, effects);
    queue.push({ nodeId, effect });
  };

  enqueue(startNodeId, initialEffect);

  while (queue.length > 0) {
    const current = queue.shift()!;
    const node = nodesById.get(current.nodeId);
    if (!node) continue;

    const modifier = effectOf(node.data.interaction);
    const effectAfterNode = modifier === NO_EFFECT ? current.effect : modifier;
    const targets = outgoingByNodeId.get(node.id) ?? [];

    if (targets.length === 0) {
      terminalEffects.add(effectAfterNode);
      continue;
    }

    targets.forEach((targetId) => enqueue(targetId, effectAfterNode));
  }

  return terminalEffects;
};

// Branches that all run: a branch that changes nothing only survives if every
// branch can change nothing.
export const combineParallelBranchEffects = (
  branchEffects: Set<string>[],
): Set<string> => {
  if (branchEffects.some((effects) => effects.size === 0)) return new Set();

  const combinedEffects = new Set<string>();
  let everyBranchCanLeaveStateUnchanged = branchEffects.length > 0;

  branchEffects.forEach((effects) => {
    everyBranchCanLeaveStateUnchanged &&= effects.has(NO_EFFECT);
    effects.forEach((effect) => {
      if (effect !== NO_EFFECT) combinedEffects.add(effect);
    });
  });

  if (everyBranchCanLeaveStateUnchanged) combinedEffects.add(NO_EFFECT);
  return combinedEffects;
};

const getRootTerminalEffects = (
  sequence: ISequence,
  rootId: string,
  effectOf: InteractionEffect,
  parallel: boolean,
): Set<string> => {
  const root = sequence.nodes?.find((node) => node.id === rootId);
  if (!root) return new Set();

  const rootEffect = effectOf(root.data.interaction);
  const targets = getOutgoingByNodeId(sequence).get(rootId) ?? [];
  if (targets.length === 0) return new Set([rootEffect]);

  const branchEffects = targets.map((targetId) =>
    getSequenceBranchTerminalEffects(sequence, targetId, effectOf, rootEffect),
  );
  return parallel
    ? combineParallelBranchEffects(branchEffects)
    : new Set(branchEffects.flatMap((effects) => [...effects]));
};

/** The effects a completed sequence can leave, from its Sequence roots (or its triggers). */
export const getSequenceTerminalEffects = (
  sequence: ISequence,
  effectOf: InteractionEffect,
): Set<string> => {
  const nodes = sequence.nodes ?? [];
  const sequenceRoots = nodes.filter(
    (node) => node.data.interaction.type === InteractionType.Sequence,
  );
  const triggerRoots = nodes.filter(
    (node) => node.data.interaction.type === InteractionType.Trigger,
  );
  const roots = sequenceRoots.length > 0 ? sequenceRoots : triggerRoots;
  const terminalEffects = new Set<string>();

  roots.forEach((root) => {
    const rootInteraction = root.data.interaction as Partial<ISequence>;
    const finishAll = rootInteraction.finishAll ?? sequence.finishAll ?? true;
    const parallel =
      root.data.interaction.type === InteractionType.Sequence && finishAll;
    getRootTerminalEffects(sequence, root.id, effectOf, parallel).forEach(
      (effect) => terminalEffects.add(effect),
    );
  });

  return terminalEffects;
};

/** The effects of one trigger's graph; all of its branches run. */
export const getTriggerTerminalEffects = (
  sequence: ISequence,
  triggerNodeId: string,
  effectOf: InteractionEffect,
): Set<string> => getRootTerminalEffects(sequence, triggerNodeId, effectOf, true);
